import type { CompanyProfile, HistoryItem, TaskView } from "@scoach/types";
import { companySlug } from "@scoach/types";
import { Modal, useToast } from "@scoach/ui";
import { Search, Trash, User as UserIcon } from "@scoach/ui/icons";
import { useEffect, useMemo, useState } from "react";

import { useAuthStore } from "../../auth/store.ts";
import { CompanyProfileCard } from "../../companies/CompanyProfileCard.tsx";
import { companiesApi } from "../../companies/api.ts";
import { dashboardApi } from "../../dashboard/api.ts";
import { DashHeader } from "../../dashboard/components/DashHeader.tsx";
import { CompanyLogo } from "../../discovery/CompanyLogo.tsx";
import { tasksApi } from "../../tasks/api.ts";
import { TaskRow } from "../../tasks/components/TaskRow.tsx";
import { clientsApi } from "../api.ts";

interface ClientInfo {
  name: string;
  meetingCount: number;
  openTasks: number;
  lastMeeting: string;
  avatar: string;
  /** Domain/website from the client's most recent meeting (for the logo). */
  domain?: string;
}

export function ClientsScreen() {
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [tasks, setTasks] = useState<TaskView[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // light:true skips the per-meeting action-item enrichment (N+1) — this screen
    // only needs meeting counts / last-meeting / domain from history, plus open-task
    // counts from tasksApi.
    Promise.all([dashboardApi.fetchHistory({ light: true }), tasksApi.list()])
      .then(([hist, t]) => {
        setHistory(hist.items);
        setTasks(t.items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const hiddenClients = useMemo(
    () => new Set(user?.settings.hiddenClients ?? []),
    [user?.settings.hiddenClients],
  );

  const clients = useMemo(() => {
    const map = new Map<string, ClientInfo>();

    for (const m of history) {
      if (hiddenClients.has(m.client)) continue;
      const existing = map.get(m.client);
      if (existing) {
        existing.meetingCount++;
        if (m.date > existing.lastMeeting) {
          existing.lastMeeting = m.date;
          if (m.website) existing.domain = m.website;
        } else if (!existing.domain && m.website) {
          existing.domain = m.website;
        }
      } else {
        map.set(m.client, {
          name: m.client,
          meetingCount: 1,
          openTasks: 0,
          lastMeeting: m.date,
          avatar: m.avatar ?? "#1A73E8",
          ...(m.website ? { domain: m.website } : {}),
        });
      }
    }

    for (const t of tasks) {
      if (!t.done) {
        const c = map.get(t.client);
        if (c) c.openTasks++;
      }
    }

    let result = Array.from(map.values());
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(q));
    }
    return result.sort((a, b) => b.meetingCount - a.meetingCount);
  }, [history, tasks, search, hiddenClients]);

  // --- Company profile dossier ---
  const [profileFor, setProfileFor] = useState<string | null>(null);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);

  function openProfile(name: string) {
    setProfileFor(name);
    setProfile(null);
    setProfileLoading(true);
    companiesApi
      .get(companySlug(name))
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setProfileLoading(false));
  }

  async function researchProfile(refresh = false) {
    if (!profileFor) return;
    setProfileBusy(true);
    try {
      setProfile(await companiesApi.research({ name: profileFor, refresh }));
    } catch {
      /* surfaced as no-profile state */
    } finally {
      setProfileBusy(false);
    }
  }

  // --- Rename client ---
  const [renameFor, setRenameFor] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  function openRename(name: string) {
    setRenameFor(name);
    setRenameDraft(name);
  }

  async function doRename() {
    const oldName = renameFor;
    const newName = renameDraft.trim();
    if (!oldName || !newName || newName === oldName) {
      setRenameFor(null);
      return;
    }
    setRenameBusy(true);
    try {
      await clientsApi.rename({ oldName, newName });
      setHistory((prev) => prev.map((h) => (h.client === oldName ? { ...h, client: newName } : h)));
      setTasks((prev) => prev.map((t) => (t.client === oldName ? { ...t, client: newName } : t)));
      toast.push({ tone: "success", message: `Renamed “${oldName}” to “${newName}”.` });
      setRenameFor(null);
    } catch (err) {
      toast.push({ tone: "error", message: `Rename failed: ${(err as Error).message}` });
    } finally {
      setRenameBusy(false);
    }
  }

  // --- View client tasks ---
  const [tasksFor, setTasksFor] = useState<string | null>(null);
  const [clientTasks, setClientTasks] = useState<TaskView[]>([]);
  const [clientTasksLoading, setClientTasksLoading] = useState(false);

  function openTasks(name: string) {
    setTasksFor(name);
    setClientTasks([]);
    setClientTasksLoading(true);
    tasksApi
      .list({ client: name })
      .then((r) => setClientTasks(r.items))
      .catch(() => setClientTasks([]))
      .finally(() => setClientTasksLoading(false));
  }

  function toggleClientTask(task: TaskView, done: boolean) {
    setClientTasks((prev) => prev.map((t) => (t.taskId === task.taskId ? { ...t, done } : t)));
    void tasksApi.updateTask(task.taskId, { done }).catch(() => {
      setClientTasks((prev) =>
        prev.map((t) => (t.taskId === task.taskId ? { ...t, done: !done } : t)),
      );
    });
  }
  function updateClientTask(
    task: TaskView,
    patch: Partial<Pick<TaskView, "who" | "what" | "due">>,
  ) {
    const prevVals = { who: task.who, what: task.what, due: task.due };
    setClientTasks((prev) => prev.map((t) => (t.taskId === task.taskId ? { ...t, ...patch } : t)));
    void tasksApi.updateTask(task.taskId, patch).catch(() => {
      setClientTasks((prev) =>
        prev.map((t) => (t.taskId === task.taskId ? { ...t, ...prevVals } : t)),
      );
    });
  }

  // --- Delete client (clear tasks + hide) ---
  const [deleteFor, setDeleteFor] = useState<string | null>(null);
  const [deleteTasks, setDeleteTasks] = useState<TaskView[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  function openDelete(name: string) {
    setDeleteFor(name);
    setDeleteTasks([]);
    setDeleteLoading(true);
    tasksApi
      .list({ client: name })
      .then((r) => setDeleteTasks(r.items))
      .catch(() => setDeleteTasks([]))
      .finally(() => setDeleteLoading(false));
  }

  async function doDelete() {
    const name = deleteFor;
    if (!name) return;
    setDeleteBusy(true);
    try {
      const res = await clientsApi.remove({ name });
      if (user) {
        setUser({
          ...user,
          settings: { ...user.settings, hiddenClients: res.hiddenClients },
        });
      }
      toast.push({
        tone: "success",
        message: `Deleted “${name}” and ${res.clearedTasks} task${
          res.clearedTasks === 1 ? "" : "s"
        }.`,
      });
      setDeleteFor(null);
    } catch (err) {
      toast.push({ tone: "error", message: `Delete failed: ${(err as Error).message}` });
    } finally {
      setDeleteBusy(false);
    }
  }

  function formatDate(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  // Small role="button" action to avoid nesting <button> inside the card button.
  function CardAction({
    label,
    onClick,
    danger,
  }: {
    label: string;
    onClick: () => void;
    danger?: boolean;
  }) {
    return (
      <span
        role="button"
        tabIndex={0}
        className="ghost-btn xs"
        style={danger ? { color: "var(--gc-red, #d93025)" } : undefined}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.stopPropagation();
            onClick();
          }
        }}
      >
        {label}
      </span>
    );
  }

  return (
    <div className="dash">
      <DashHeader />
      <div className="clients-body">
        <div className="clients-header">
          <h2 className="clients-title">
            <UserIcon size={20} /> Clients
          </h2>
          <span className="clients-count">{clients.length} clients</span>
          <div className="clients-search">
            <Search size={14} />
            <input
              type="search"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="tasks-empty">Loading clients...</div>
        ) : clients.length === 0 ? (
          <div className="tasks-empty">
            {search
              ? "No clients match your search."
              : "No clients yet. Start a meeting to see clients here."}
          </div>
        ) : (
          <div className="clients-table">
            <div className="clients-row clients-head-row">
              <div>Client</div>
              <div>Meetings</div>
              <div>Open tasks</div>
              <div>Last meeting</div>
              <div />
            </div>
            {clients.map((c) => (
              <div
                key={c.name}
                className="clients-row"
                role="button"
                tabIndex={0}
                onClick={() => openTasks(c.name)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") openTasks(c.name);
                }}
              >
                <div className="clients-client">
                  <CompanyLogo name={c.name} domain={c.domain} size={30} rounded={8} />
                  <span className="clients-client-name">{c.name}</span>
                </div>
                <div className="clients-num">{c.meetingCount}</div>
                <div className="clients-num">
                  {c.openTasks > 0 ? (
                    <span className="action-badge">{c.openTasks}</span>
                  ) : (
                    <span className="action-badge empty">—</span>
                  )}
                </div>
                <div className="clients-date mono">{formatDate(c.lastMeeting)}</div>
                <div className="clients-actions" onClick={(e) => e.stopPropagation()}>
                  <CardAction label="Profile" onClick={() => openProfile(c.name)} />
                  <CardAction label="Tasks" onClick={() => openTasks(c.name)} />
                  <CardAction label="Rename" onClick={() => openRename(c.name)} />
                  <CardAction label="Delete" danger onClick={() => openDelete(c.name)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={profileFor !== null}
        onClose={() => setProfileFor(null)}
        title={`Company profile — ${profileFor ?? ""}`}
        width={640}
      >
        <div style={{ padding: 16 }}>
          {profileLoading ? (
            <div className="tasks-empty">Loading profile…</div>
          ) : profile ? (
            <CompanyProfileCard
              profile={profile}
              onRefresh={() => researchProfile(true)}
              refreshing={profileBusy}
            />
          ) : (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <p style={{ color: "var(--text-3)", marginBottom: 12 }}>
                No researched profile yet for {profileFor}.
              </p>
              <button
                type="button"
                className="pill-btn primary"
                onClick={() => researchProfile(false)}
                disabled={profileBusy}
              >
                {profileBusy ? "Researching…" : "Research company"}
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* Rename client */}
      <Modal
        open={renameFor !== null}
        onClose={() => setRenameFor(null)}
        title="Rename client"
        width={440}
      >
        <div style={{ padding: 16 }}>
          <p style={{ color: "var(--text-3)", marginTop: 0, marginBottom: 12 }}>
            This updates the client name on every meeting for “{renameFor}”.
          </p>
          <input
            type="text"
            className="task-edit-input"
            style={{ width: "100%", boxSizing: "border-box" }}
            value={renameDraft}
            autoFocus
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void doRename();
              if (e.key === "Escape") setRenameFor(null);
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button type="button" className="pill-btn" onClick={() => setRenameFor(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="pill-btn primary"
              onClick={() => void doRename()}
              disabled={renameBusy || !renameDraft.trim() || renameDraft.trim() === renameFor}
            >
              {renameBusy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      {/* View client tasks */}
      <Modal
        open={tasksFor !== null}
        onClose={() => setTasksFor(null)}
        title={`Tasks — ${tasksFor ?? ""}`}
        width={640}
      >
        <div style={{ padding: 16, maxHeight: "70vh", overflow: "auto" }}>
          {clientTasksLoading ? (
            <div className="tasks-empty">Loading tasks…</div>
          ) : clientTasks.length === 0 ? (
            <div className="tasks-empty">No tasks for this client.</div>
          ) : (
            <div className="tasks-cards">
              {clientTasks.map((t) => (
                <TaskRow
                  key={t.taskId}
                  task={t}
                  showClient={false}
                  onToggle={toggleClientTask}
                  onUpdate={updateClientTask}
                />
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Delete client confirmation */}
      <Modal
        open={deleteFor !== null}
        onClose={() => setDeleteFor(null)}
        title={`Delete client — ${deleteFor ?? ""}`}
        width={560}
      >
        <div style={{ padding: 16 }}>
          <p style={{ marginTop: 0 }}>
            This hides “{deleteFor}” from your Clients page and permanently deletes all of its
            tasks. Meetings and summaries are kept and stay available in your history.
          </p>
          {deleteLoading ? (
            <div className="tasks-empty">Loading tasks…</div>
          ) : deleteTasks.length === 0 ? (
            <p style={{ color: "var(--text-3)" }}>This client has no tasks.</p>
          ) : (
            <>
              <p style={{ fontWeight: 600, marginBottom: 6 }}>
                The following {deleteTasks.length} task{deleteTasks.length === 1 ? "" : "s"} will be
                deleted:
              </p>
              <ul
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "grid",
                  gap: 6,
                  maxHeight: "40vh",
                  overflowY: "auto",
                }}
              >
                {deleteTasks.map((t) => (
                  <li
                    key={t.taskId}
                    style={{
                      padding: "6px 10px",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{t.what || "(no description)"}</div>
                    <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                      {[t.who, t.due, t.meetingTitle].filter(Boolean).join(" · ")}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button type="button" className="pill-btn" onClick={() => setDeleteFor(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="pill-btn"
              style={{
                background: "var(--gc-red, #d93025)",
                color: "#fff",
                borderColor: "transparent",
              }}
              onClick={() => void doDelete()}
              disabled={deleteBusy || deleteLoading}
            >
              <Trash size={13} /> {deleteBusy ? "Deleting…" : "Delete client"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
